<template>
  <div class="DropdownMenuStandard">
    <div v-for="block in menuBlocks"
      :key="`${block.row},${block.column}`"
      :style="{ gridRow: block.row, gridColumn: block.column }"
    >
      <MenuGroupHeading v-if="block.title" :title="block.title" />
      <div v-if="block.items" class="groupItems" :class="block.layout">
        <div v-for="item in block.items" :key="item.title" :class="item.isHeading ? 'groupHeading' : 'groupEntry'">
          <MenuGroupHeading v-if="item.isHeading" :title="item.title" />
          <MenuGroupItem v-else class="menuItem" :class="item.description && 'verbose'" v-bind="item" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MenuGroupHeading from '../shared/MenuGroupHeading.vue'
import MenuGroupItem from '../shared/MenuGroupItem.vue'

// Utility function to split an array into a given number of chunks
// E.g., splitArray([1, 2, 3, 4, 5, 6], 3) => [[1, 2], [3, 4], [5, 6]]
function splitArray(ary, count) {
  const result = []
  const chunkLength = Math.ceil(ary.length / count)
  for (let i = 0; i < ary.length; i += chunkLength) {
    const chunk = ary.slice(i, i + chunkLength)
    result.push(chunk)
  }
  return result
}

// Take an array of the entry groupings and turn it into a data structure we can iterate over
// in the Vue template. Mostly this involves looking at the 'layout' field of each grouping and
// either splitting the grouping entries across two columns (when layout is 'double-column'),
// keeping them in one column (when layout is 'single-column'), or combining two groupings into
// a single column (when two successive groupings have a layout of 'half-column').
function layoutMenuEntries(groupings) {

  const result = []
  var columnOffset = 0

  function appendHeadingItemsPair(heading, ...itemArrays) {
    result.push(
      {
        row: 1,
        column: 1 + columnOffset,
        title: heading,
      },
      ...itemArrays.map((items, index) => ({
        row: 2,
        column: index + 1 + columnOffset,
        items,
      })),
    )
    columnOffset += itemArrays.length
  }

  for (let i = 0; i < groupings.length; i++) {
    const grouping = groupings[i]
    switch (grouping.layout) {
    case 'half-column':
      if (groupings[i + 1]?.layout === 'half-column') {
        const nextGrouping = groupings[++i]
        appendHeadingItemsPair(grouping.title, [
          ...grouping.entries,
          { isHeading: true, title: nextGrouping.title },
          ...nextGrouping.entries,
        ])
      }
      else {
        appendHeadingItemsPair(grouping.title, grouping.entries)
      }
      break;
    case 'single-column':
      appendHeadingItemsPair(grouping.title, grouping.entries)
      break;
    case 'double-column':
      appendHeadingItemsPair(grouping.title, ...splitArray(grouping.entries, 2))
      break;
    }
  }

  return result
}

export default {
  components: {
    MenuGroupHeading,
    MenuGroupItem,
  },
  props: {
    groupings: {
      type: Array,
      required: true,
    },
  },
  computed: {
    menuBlocks() {
      return layoutMenuEntries(this.groupings)
    },
  },
}
</script>

<style scoped>
.DropdownMenuStandard {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: var(--spacing-8);
  column-gap: var(--spacing-9);
}
.menuItem {
  --title-font-size: var(--font-size-2);
}
.menuItem.verbose {
  min-height: 4.5rem;
}
.groupEntry {
  max-width: 15rem;
}
.groupHeading + .groupEntry {
  margin-top: var(--spacing-5);
}
.groupEntry + .groupEntry {
  margin-top: var(--spacing-8);
}
.groupEntry + .groupHeading {
  margin-top: var(--spacing-10);
}
</style>
